import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { trackAppcuesEvent } from "@expert/monitoring";
import { CustomerPanel as DecoupledCustomerPanel } from "@expert/sales-tools";
import { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { useEligibilityPrecheck } from "@/sales-tools/home-product/hooks/useEligibilityPrecheck";
import { useSessionId } from "@/sdk";
import { useLogger } from "@/sdk/logging";

import { useDigitalCompetencyListener } from "./components/customer-insights/digital-competency";
import { CustomerPanel } from "./components/customer-panel/CustomerPanel";

export function CustomerPanelWrapper() {
    const { features } = useFeatures<WorkspaceFeatures>();
    const logger = useLogger({ module: "CustomerPanelWrapper" });

    if (features.isUsingDecoupledSalesComponents) {
        return (
            <ErrorBoundary
                fallback={<CustomerPanel />}
                onError={(error) => {
                    logger.error({ error }, "Decoupled CustomerPanel crashed, falling back to original");
                }}
            >
                <DecoupledCustomerPanelHost />
            </ErrorBoundary>
        );
    }

    return <CustomerPanel />;
}

/**
 * Host shim for the decoupled CustomerPanel. Runs the side-effect hooks the
 * legacy CustomerPanel runs internally (digital competency listener, eligibility
 * precheck, Appcues mount event) so the decoupled path has parity.
 */
function DecoupledCustomerPanelHost() {
    const sessionId = useSessionId();
    useDigitalCompetencyListener();
    useEligibilityPrecheck(sessionId);
    useEffect(() => {
        trackAppcuesEvent("CustomerPanelVisible", {});
    }, []);

    return <DecoupledCustomerPanel />;
}
